import { buildCollection, buildProperty } from "@firecms/core";
import { HomeButton } from "../components/HomeButton";

export type BadgeDefinitionType = {
    name: string;
    description: string;
    category: string;
    tier: string;
    criteria: Record<string, any>;
    icon_url: string;
    is_nft_eligible: boolean;
    is_soulbound: boolean;
    created_at: Date;
    updated_at: Date;
};

export const badgeDefinitionsCollection = buildCollection<BadgeDefinitionType>({
    id: "badge_definitions",
    name: "Badge Definitions",
    singularName: "Badge Definition",
    path: "badge_definitions",
    icon: "EmojiEvents",
    group: "NFT",
    Actions: HomeButton,
    description:
        "Badge definitions (read-only). Use the Badge Definitions view under NFT to create, edit, or delete.",
    permissions: {
        edit: false,
        create: false,
        delete: false,
    },
    properties: {
        name: buildProperty({
            name: "Badge Name",
            dataType: "string",
            validation: { required: true },
            description: "Display name shown to users (e.g. 'Week Warrior')",
        }),
        description: buildProperty({
            name: "Description",
            dataType: "string",
            validation: { required: true },
            description: "How to earn this badge (e.g. 'Maintain a 7-day streak')",
        }),
        category: buildProperty({
            name: "Category",
            dataType: "string",
            validation: { required: true },
            enumValues: {
                streak: "🔥 Streak",
                session: "📚 Session",
                skill: "🎯 Skill",
                cefr: "🎓 CEFR",
                social: "🤝 Social",
                special: "⭐ Special",
            },
        }),
        tier: buildProperty({
            name: "Tier",
            dataType: "string",
            validation: { required: true },
            enumValues: {
                bronze: "🥉 Bronze",
                silver: "🥈 Silver",
                gold: "🥇 Gold",
                platinum: "💎 Platinum",
                legendary: "🏆 Legendary",
            },
        }),
        criteria: buildProperty({
            name: "Criteria",
            dataType: "map",
            description:
                "Criteria map for awarding. Keys: streak, total_sessions, cefr_level, referrals, shares, first_roleplay, first_study, first_assessment, first_voice, early_adopter",
            keyValue: true,
        }),
        icon_url: buildProperty({
            name: "Badge Image",
            dataType: "string",
            storage: {
                storagePath: "badge_images",
                acceptedFiles: ["image/png", "image/jpeg", "image/svg+xml", "image/webp"],
                maxSize: 5 * 1024 * 1024,
                storeUrl: true,
            },
            description: "Square image recommended (1080×1080). Used as the NFT image on-chain.",
        }),
        is_nft_eligible: buildProperty({
            name: "NFT Eligible",
            dataType: "boolean",
            description: "Can this badge be minted as an NFT on TON?",
            defaultValue: false,
            columnWidth: 120,
        }),
        is_soulbound: buildProperty({
            name: "Soulbound (SBT)",
            dataType: "boolean",
            description: "Non-transferable when minted as NFT?",
            defaultValue: false,
            columnWidth: 120,
        }),
        created_at: buildProperty({
            name: "Created At",
            dataType: "date",
            mode: "date_time",
            autoValue: "on_create",
        }),
        updated_at: buildProperty({
            name: "Updated At",
            dataType: "date",
            mode: "date_time",
            autoValue: "on_update",
        }),
    },
    propertiesOrder: [
        "name",
        "description",
        "category",
        "tier",
        "criteria",
        "icon_url",
        "is_nft_eligible",
        "is_soulbound",
        "created_at",
        "updated_at",
    ],
});
